import { ReactNode } from 'react';
import {
  BopisRecipientDetail,
  Address,
  ServiceLocation,
  CheckoutEntry,
  ICheckoutPickupStoresOptions,
  ICheckoutAnyType,
} from '@commerce-checkout-webcomponents/sdk';
import { LineItem, BundleItem } from '@commerce-fulfillment-webcomponents/sdk';
import { Contact, downlineAddressType } from '@commerce-profile-webcomponents/sdk';

import { Cart } from '@commerce-cart-webcomponents/sdk';
import { DataFlowEnum } from '../../common/types/CheckoutType';

export interface ICheckoutStoreIconProps {
  iconUrl?: string;
}

export interface IDownlineAddressListMap {
  [key: string]: string;
}

export interface ICheckoutShippingAddressDetailsProps {
  deliveryAddress?: ((Address & ContactType) | Contact) | null;
  storePickupAddress?: (ServiceLocation & ICheckoutStoreIconProps) | null;
  downlineAddressList?: downlineAddressType[];
  downlineAddressListMap?: IDownlineAddressListMap;
  onDeliveryAddressConfirmClick?: (args: (Address & ContactType) | Contact) => void;
  onAddressSelectionConfirmClick?: (shippingType: string) => void;
  bopisDetails?: BopisRecipientDetail;
  entries?: Array<CheckoutEntry>;
  currencyIsoCode?: string;
  onPickupAddressConfirmClick?: (
    pickupStoreData: ServiceLocation,
    selectedStoreOption: string,
    bopisRecipientDetail?: BopisRecipientDetail,
  ) => void;
  shippingTypeChangeFlow?: boolean;
  pickupContactFieldEnabled?: boolean;
  typePickupDisabled?: boolean;
  typeDeliveryDisabled?: boolean;
  shippingType: string;
  isPickupAddressSelectable?: boolean;
  isDeliveryAddressSelectable?: boolean;
  cartData?: Cart | undefined;
  fulfillmentId?: string;
  isDeliveryAddressEditable?: boolean;
}

export interface ICheckoutShippingDetailsContainerProps {
  dataFlow: DataFlowEnum;
  data?: ICheckoutShippingAddressDetailsProps;
}
export interface ContactType {
  contactType?: string;
}
export interface ICheckoutIsSaved {
  isSaved?: boolean;
}
export enum ContactTypeEnum {
  SHIPPING = 'SHIPPING',
}

export interface ICheckoutPickupDrawerProps {
  onCloseDrawer: (args?: boolean) => void;
  onConfrimPickupStore: (
    bopisDetails: BopisRecipientDetail,
    storeOptionName: string,
    serviceLocation: ServiceLocation,
  ) => void;
  selectedStoreForPickup?: (ServiceLocation & ICheckoutStoreIconProps) | null | undefined;
  bopisDetails?: BopisRecipientDetail;
  entries?: Array<CheckoutEntry>;
  currencyIsoCode?: string;
  contactFieldEnabled?: boolean;
  confirmButtonLoading?: boolean;
  locationPermission: string;
  setLocationPermission: (permissionStatus: string) => void;
  showAvailabilityTag?: boolean;
  dataFlow?: DataFlowEnum;
}

export interface ICheckoutPickupContactDrawerProps {
  onCloseDrawer: () => void;
  onConfrimPickupContact: (bopisDetails: BopisRecipientDetail) => void;
  bopisDetails?: BopisRecipientDetail;
  confirmButtonLoading?: boolean;
}

export interface ICheckoutShippingTypeProps {
  shippingType?: string;
  onChangeCB: (shippingType: string) => void;
  isPickupDisabled?: boolean;
  isDeliveryDisabled?: boolean;
  dataFlow?: DataFlowEnum;
  columnView?: boolean;
}

export interface ICheckoutShippingConfirmedAddressLinesProps {
  title?: string;
  subTitle?: string;
  storeId?: string;
  imageUrl?: string;
  addressValues?: Array<string>;
  instructions?: string;
  recipientName?: string;
  recipientContact?: string;
  pickupRecipientName?: string;
  pickupRecipientEmail?: string;
}

export interface ICheckoutShippingConfirmedAddressProps {
  shippingTagTitle?: string;
  shippingTagIcon?: string;
  shippingTagLabel?: string;
  addressFields?: Array<ICheckoutShippingConfirmedAddressLinesProps>;
  isSaved?: boolean;
  profileName?: string;
}

export interface ICheckoutShippingSelectedAddressFieldsProps {
  label?: string;
  value?: Array<string>;
}

export interface ICheckoutShippingSelectedAddressProps {
  heading?: string;
  title?: string;
  storeId?: string;
  imageUrl?: string;
  addressValues?: Array<string>;
  instructions?: string;
  onEditIconClick?: () => void;
  extraFields?: Array<ICheckoutShippingSelectedAddressFieldsProps>;
  recipientName?: string;
  recipientContact?: string;
  recipientEmail?: string;
}

export interface ICheckoutSelectedAddressCardProps {
  selectedAddress?: ICheckoutShippingSelectedAddressProps;
  selectedStoreOptionName?: string;
  softFilters?: ISoftFilters;
  shippingType?: string;
  onEditIconClick: () => void;
  profileName?: string;
  hideEdit?: boolean;
  children?: ReactNode | ReactNode[];
}

export interface ICheckoutConfirmedAddressDetailsProps {
  confirmedAddress?: ICheckoutShippingConfirmedAddressProps;
  shippingType?: string;
  children?: ReactNode | ReactNode[];
}

export interface ICheckoutAddNewAddressProps {
  newAddressType: string;
  softFilters?: ISoftFilters;
  addNewAddressCallback: () => void;
  addNewDeliveryAddressCtaHandler?: () => void;
  isLoading?: boolean;
  ownerId?: string;
  isCustomView?: boolean;
  heading?: string;
  ctaText?: string;
}

export interface ICheckoutFulfillmentEntries {
  lineItems: Array<LineItem>;
  bundleItems: Array<BundleItem>;
}

export interface ICheckoutStoreOptionRadioCardProps {
  cardData: ICheckoutPickupStoresOptions;
  isSelected: boolean;
  showAvailabilityTag?: boolean;
  onCardClickCallback?: (args: string) => void;
  dispatchAction: React.Dispatch<{
    type: string;
    payload?: string | boolean | object | undefined;
  }>;
  distance?: string;
}

export interface ICheckoutLocationHeaderProps {
  onConfirmClick: () => void;
}

export interface ISoftFilters {
  delivery: ISoftFiltersObject;
  pickup: ISoftFiltersObject;
}
export interface ISoftFiltersObject {
  message: string;
  isVisible: boolean;
}

export interface IShippingInitialState {
  shippingDetailsData: ICheckoutShippingAddressDetailsProps | null;
  displayAddNewAddressForDelivery: boolean;
  displayAddNewAddressForPickup: boolean;
  displayConfirmedAddress: boolean;
  displayAddressSelection: boolean;
  displayAddressBook: boolean;
  isDeliveryAddressAvailable: boolean;
  isPickupAddressAvailable: boolean;
  deliveryAddress: ((Address & ContactType) | Contact) | null;
  storePickupAddress: (ServiceLocation & ICheckoutStoreIconProps) | null;
  shippingType: string;
  softFilters: ISoftFilters;
  locationPermission: string;
  isAddressSavedFlag: boolean;
  fulfillmentTypeForpickup: string;
  shippingDetailsUpdating: boolean;
}

export interface ICheckoutStoreDetailsDrawerContentProps {
  imgSrc?: string;
  imgAlt?: string;
  storeDetailsData: ICheckoutAnyType;
}
